export interface ProviderDraft {
  id: string;
  name: string;
  type: string;
  baseURL?: string;
  apiKey: string;
  hasApiKey: boolean;
  model: string;
  pricePerMTokenIn?: number;
  pricePerMTokenOut?: number;
}

export interface ProviderIssue {
  id: string;
  field: string;
  message: string;
}

export function slugifyId(name: string, taken: string[]): string {
  const base = name
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (!base) return "";
  if (!taken.includes(base)) return base;

  let suffix = 2;
  while (taken.includes(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
}

const isBlank = (value: string | undefined): boolean =>
  !value || value.trim().length === 0;

const badPrice = (value: number | undefined): boolean =>
  value !== undefined && (!Number.isFinite(value) || value < 0);

export function validateProviders(
  providers: ProviderDraft[]
): ProviderIssue[] {
  const issues: ProviderIssue[] = [];
  const seen = new Set<string>();

  for (const provider of providers) {
    const at = (field: string, message: string) =>
      issues.push({ id: provider.id, field, message });

    if (isBlank(provider.name)) at("name", "Informe um nome.");
    if (isBlank(provider.id)) at("name", "Informe um nome para gerar o id.");
    else if (seen.has(provider.id)) at("name", "Já existe um provider com esse nome.");
    else seen.add(provider.id);

    if (isBlank(provider.baseURL)) at("baseURL", "Informe a base URL.");
    if (isBlank(provider.model)) at("model", "Informe o modelo.");
    if (isBlank(provider.apiKey) && !provider.hasApiKey) {
      at("apiKey", "Informe a chave de API.");
    }
    if (badPrice(provider.pricePerMTokenIn)) {
      at("pricePerMTokenIn", "Preço inválido.");
    }
    if (badPrice(provider.pricePerMTokenOut)) {
      at("pricePerMTokenOut", "Preço inválido.");
    }
  }

  return issues;
}

const SERVER_PATTERNS = [
  /^Provider (.+) sem (name|apiKey|model|baseURL)\.$/,
  /^Provider (.+) com (type|enabled|priority|pricePerMTokenIn|pricePerMTokenOut) inválido\.$/,
];

export function parseServerIssue(message: string): ProviderIssue | null {
  for (const pattern of SERVER_PATTERNS) {
    const match = pattern.exec(message);
    if (match) return { id: match[1], field: match[2], message };
  }

  const duplicated = /^Provider (.+) duplicado\.$/.exec(message);
  if (duplicated) return { id: duplicated[1], field: "name", message };

  return null;
}

export function issueKey(id: string, field: string): string {
  return `${id}:${field}`;
}

export function toIssueMap(issues: ProviderIssue[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const issue of issues) {
    map[issueKey(issue.id, issue.field)] ??= issue.message;
  }
  return map;
}
